import {
  createOperationKey,
  fetchOperationStatus,
} from "@/lib/moneyOperations";
import type { ActionDirective, OperationRecoveryState } from "./types";

export const OFFLINE_MUTATION_MESSAGE =
  "ตอนนี้ออฟไลน์ — ดูข้อมูลล่าสุดที่ซิงก์ไว้ได้ แต่ยังทำรายการเงินไม่ได้";

const DEFAULT_FAILED_MESSAGE = "ข้อมูลยังไม่เปลี่ยนแปลง ลองอีกครั้งได้เลย";

/**
 * Map technical / English API errors to short Thai copy for the recovery panel.
 * Messages that are already Thai pass through.
 */
export function formatFinancialWorkErrorMessage(
  message: string | null | undefined,
): string {
  const trimmed = String(message ?? "").trim();
  if (!trimmed) return DEFAULT_FAILED_MESSAGE;

  const hasThai = /[\u0E00-\u0E7F]/.test(trimmed);
  const looksTechnical =
    /record_persist|announcement|income row|not found|not created|match failed|timed?\s*out|failed to fetch|_persist|_order|operation lookup|financial work action failed/i.test(
      trimmed,
    );
  if (hasThai && !looksTechnical) return trimmed;

  if (
    /announcement order not found|receivable(?:_order)? not found|order not found/i.test(
      trimmed,
    )
  ) {
    return "ไม่พบยอดค้างที่ต้องการจับคู่ — โหลดรายการใหม่แล้วลองอีกครั้ง";
  }
  if (
    /income row was not created|record_persist failed/i.test(trimmed)
  ) {
    return "บันทึกรายรับไม่สำเร็จ — ลองใหม่อีกครั้ง หรือตรวจสลิปแล้วจับคู่ใหม่";
  }
  if (/payment(?:_order)? not found|slip not found/i.test(trimmed)) {
    return "ไม่พบสลิปที่เลือก — โหลดรายการใหม่แล้วลองอีกครั้ง";
  }
  if (/already matched|already allocated|already paid/i.test(trimmed)) {
    return "รายการนี้ถูกจับคู่ไปแล้ว";
  }
  if (/revision|stale|conflict/i.test(trimmed)) {
    return hasThai
      ? trimmed
      : "ข้อมูลรายการเปลี่ยนแล้ว — โหลดใหม่แล้วลองอีกครั้ง";
  }
  if (/timeout|timed?\s*out|network|failed to fetch/i.test(trimmed)) {
    return "การเชื่อมต่อมีปัญหา — ลองใหม่อีกครั้ง";
  }
  if (/offline/i.test(trimmed)) {
    return OFFLINE_MUTATION_MESSAGE;
  }
  if (/owner_user_id is required|at least one payment/i.test(trimmed)) {
    return "ข้อมูลไม่ครบสำหรับจับคู่ — ลองเลือกสลิปใหม่";
  }
  if (/match failed/i.test(trimmed)) {
    return "จับคู่ไม่สำเร็จ — ลองใหม่อีกครั้ง";
  }
  if (/financial work action failed|operation lookup failed/i.test(trimmed)) {
    return "ทำรายการไม่สำเร็จ — ลองใหม่อีกครั้ง";
  }
  if (
    looksTechnical ||
    /^[a-z0-9_.:\-/\s()]+$/i.test(trimmed)
  ) {
    return "ทำรายการไม่สำเร็จ — ลองใหม่อีกครั้ง";
  }

  return trimmed;
}

export function canMutateFinancialWork(isOnline: boolean): {
  allowed: boolean;
  reason?: string;
} {
  if (!isOnline) {
    return { allowed: false, reason: OFFLINE_MUTATION_MESSAGE };
  }
  return { allowed: true };
}

export function assertCanMutateFinancialWork(isOnline: boolean): void {
  const gate = canMutateFinancialWork(isOnline);
  if (!gate.allowed) {
    throw new Error(gate.reason || OFFLINE_MUTATION_MESSAGE);
  }
}

/** Prefer server-provided operationKey; never invent semantics — only a local retry key when absent. */
export function resolveActionOperationKey(action: ActionDirective): string {
  const fromServer = String(action.operationKey || "").trim();
  if (fromServer) return fromServer;
  return createOperationKey(action.actionType || "fw");
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * After an unknown mutation result (timeout / network drop), look up the same
 * operationKey before creating a new one.
 */
export async function recoverOperationByKey(
  operationKey: string,
  options?: {
    maxAttempts?: number;
    initialDelayMs?: number;
    onState?: (state: OperationRecoveryState) => void;
  },
): Promise<OperationRecoveryState> {
  const key = operationKey.trim();
  if (!key) {
    return {
      kind: "failed",
      operationKey: "",
      message: "ไม่พบรหัสรายการ",
      retrySafeWithSameKey: false,
    };
  }

  const maxAttempts = options?.maxAttempts ?? 5;
  let delay = options?.initialDelayMs ?? 400;

  options?.onState?.({ kind: "processing", operationKey: key });

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const status = await fetchOperationStatus(key);
      const normalized = String(status.status || "").toLowerCase();

      if (
        normalized === "processing" ||
        normalized === "pending" ||
        normalized === "in_progress"
      ) {
        options?.onState?.({
          kind: "processing",
          operationKey: key,
          message: "กำลังบันทึกรายการ…",
        });
        await sleep(delay);
        delay = Math.min(delay * 1.6, 4000);
        continue;
      }

      if (
        normalized === "committed" ||
        normalized === "applied" ||
        normalized === "replayed" ||
        normalized === "success" ||
        normalized === "succeeded"
      ) {
        const done: OperationRecoveryState = {
          kind: "committed",
          operationKey: key,
          status: status.status,
          after: status.after,
        };
        options?.onState?.(done);
        return done;
      }

      if (
        normalized === "rejected" ||
        normalized === "conflict" ||
        normalized === "failed"
      ) {
        const failed: OperationRecoveryState = {
          kind: "failed",
          operationKey: key,
          message: formatFinancialWorkErrorMessage(
            (typeof status.message === "string" && status.message.trim()) ||
              `รายการไม่สำเร็จ (${status.status})`,
          ),
          retrySafeWithSameKey: false,
        };
        options?.onState?.(failed);
        return failed;
      }

      // not_found — may still be in-flight on first attempts
      if (attempt < maxAttempts - 1) {
        options?.onState?.({
          kind: "processing",
          operationKey: key,
          message: "กำลังตรวจผลรายการ…",
        });
        await sleep(delay);
        delay = Math.min(delay * 1.6, 4000);
        continue;
      }

      const unknown: OperationRecoveryState = {
        kind: "failed",
        operationKey: key,
        message: "ยังไม่พบผลรายการ — ลองตรวจสถานะด้วยรหัสเดิม ไม่สร้างรายการใหม่",
        retrySafeWithSameKey: true,
      };
      options?.onState?.(unknown);
      return unknown;
    } catch (err) {
      if (attempt < maxAttempts - 1) {
        await sleep(delay);
        delay = Math.min(delay * 1.6, 4000);
        continue;
      }
      const failed: OperationRecoveryState = {
        kind: "failed",
        operationKey: key,
        message: formatFinancialWorkErrorMessage(
          err instanceof Error ? err.message : "ตรวจสถานะรายการไม่สำเร็จ",
        ),
        retrySafeWithSameKey: true,
      };
      options?.onState?.(failed);
      return failed;
    }
  }

  return {
    kind: "failed",
    operationKey: key,
    message: "หมดเวลารอผลรายการ — ใช้รหัสเดิมตรวจซ้ำได้",
    retrySafeWithSameKey: true,
  };
}
